import { withResolvers } from "@otto-ec/assets-core-utils/promise";
import { on, unsetAll, type Unset } from "../../event/head";
import { nanoid } from "../../misc/head";
import type { TargetStore } from "../store/store.types";
import type { dispatchSignalInternal } from "./private";
import { win } from "../../../common/namespaces";

export interface MessageBase {
  /*                                                                                */
  messageId: string;
  /*                                               */
  path: string;
}

/**
 *
 */
export interface MessageAsk extends MessageBase {
  /*                                                                    */
  type: "SIGNAL_ASK";
  /*                                             */
  detail: unknown;
  /*                                                  */
  retain?: boolean | number;
}

/**
 *
 */
export interface MessageSyn extends MessageBase {
  /*                                                                         */
  type: "SIGNAL_SYN";
}

export type MessageRetVal = [undefined, unknown] | [unknown, undefined];

/**
 *
 */
export interface MessageAck extends MessageBase {
  /*                                                                           */
  type: "SIGNAL_ACK";
  /*                                               */
  retval: MessageRetVal[];
}

/**
 *
 */
export interface MessageErr extends MessageBase {
  /*                                                                            */
  type: "SIGNAL_ERR";
  /*                                                      */
  error: unknown;
}

const messageTypes = ["SIGNAL_ASK", "SIGNAL_SYN", "SIGNAL_ACK", "SIGNAL_ERR"] as const satisfies (
  | MessageAck
  | MessageAsk
  | MessageErr
  | MessageSyn
)["type"][];

/**
 *
 */
export const createChannel = (): BroadcastChannel | null => {
  /*                                                      */
  if (import.meta.env.SSR) return null;

  /*                                                                                   */
  if (!("BroadcastChannel" in win) || typeof win.BroadcastChannel !== "function") return null;

  /*                                */
  /*                                     */
  return new BroadcastChannel("otto-nexus");
};

/**
 *
 *
 *
 */
const channel = createChannel();

/**
 *
 */
type PendingEntry = [
  timeout: number,
  resolve: (value: MessageRetVal[]) => void,
  results: (MessageRetVal[] | null)[],
];

/**
 *
 *
 */
const pending = new Map<string, PendingEntry>();

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const handleMessage = async (
  store: TargetStore,
  ackChannel: NonNullable<typeof channel>,
  dispatch: typeof dispatchSignalInternal,
  event: MessageEvent<MessageAck | MessageSyn | MessageAsk | MessageErr>,
): Promise<void> => {
  /*                                                  */
  if (event.data.path !== store.path) return;
  const { path, messageId } = event.data;
  const { log } = store;

  log.debug("Processing incomming message:", event.data.type, "id:", messageId);

  /*                                                          */
  if (event.data.type === messageTypes[0]) {
    /*                                                                                                       */
    ackChannel.postMessage({ type: messageTypes[1], path, messageId });
    /*                                                    */
    const [, retval] = dispatch(store, [event.data.detail, { retain: event.data.retain }]);

    /*                             */
    const payload: MessageRetVal[] = await Promise.all(
      retval.map(async (result) => {
        try {
          return [undefined, await result];
        } catch (error) {
          return [error, undefined];
        }
      }),
    );

    /*                                   */
    log.debug("Sending results for message:", messageId, payload);
    return ackChannel.postMessage({
      type: messageTypes[2],
      messageId,
      path,
      retval: payload,
    } satisfies MessageAck);
  }

  /*                                                        */
  if (event.data.type === messageTypes[1]) {
    const pendingEntry = pending.get(event.data.messageId);
    /*                                                 */
    if (!pendingEntry) return;

    /*                                                                                                                       */
    window.clearTimeout(pendingEntry[0]);

    /*                                                            */
    log.debug("Add pending entry for message:", messageId);
    pendingEntry[2].push(null);
  }

  /*                                          */
  if (event.data.type === messageTypes[2]) {
    const pendingEntry = pending.get(event.data.messageId);
    /*                                                 */
    if (!pendingEntry) return;

    const retvals = pendingEntry[2];

    /*                                        */
    log.debug("Add results for message:", messageId, event.data.retval);
    const nullIndex = retvals.indexOf(null);
    if (nullIndex !== -1) {
      retvals.splice(nullIndex, 1);
    }
    /*                             */
    retvals.push(event.data.retval);

    if (retvals.includes(null)) return;

    /*                                                                         */
    log.debug("Resolving pending entry for message:", messageId, retvals);
    pendingEntry[1]((retvals as MessageRetVal[][]).flat(1));
    pending.delete(event.data.messageId);
  }

  /*                                   */
  if (event.data.type === messageTypes[3]) {
    const pendingEntry = pending.get(event.data.messageId);
    /*                                                 */
    if (!pendingEntry) return;

    log.debug("Add error result for message:", messageId, event.data.error);
    pendingEntry[2].push([[event.data.error, undefined]]);
  }
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const handleMessageError = (
  store: TargetStore,
  ackChannel: NonNullable<typeof channel>,
  event: MessageEvent,
): void => {
  store.log.warn("Error during message decoding for", store.path, event);
  ackChannel.postMessage({
    type: messageTypes[2],
    path: event.data?.path,
    messageId: event.data?.messageId,
    error: new Error("Error in receiving broadcast message"),
  });
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const onMessageIfGlobal = (
  store: TargetStore,
  dispatch: typeof dispatchSignalInternal,
): Unset | undefined => {
  if (import.meta.env.SSR || !channel || !store.broadcast) return;

  store.log.debug("Init broadcast channel for path:", store.path);
  return unsetAll(
    on(channel, "message", (event) => handleMessage(store, channel, dispatch, event)),
    on(channel, "messageerror", (event) => handleMessageError(store, channel, event)),
  );
};

/**
 *
 *
 *
 *
 *
 *
 */
export const broadcastIfGlobal = (store: TargetStore): Promise<unknown> | undefined => {
  if (import.meta.env.SSR || !channel || !store.broadcast) return;

  const messageId = nanoid();
  const { path, detail, retained } = store;

  /*                                                         */
  const [result, resolve] = withResolvers<unknown>();

  /*                                                                                          */
  /*                                                                                                 */
  const timeout = window.setTimeout(() => {
    store.log.debug(
      "No response received for broadcast message, resolving with empty array. Message ID:",
      messageId,
    );
    pending.delete(messageId);
    resolve([]);
  }, 100);

  /*                                                                                                */
  /*                                    */
  /*                                                                                    */
  /*                                                       */
  pending.set(messageId, [timeout, resolve, []]);

  /*                                 */
  channel.postMessage({ type: messageTypes[0], messageId, path, detail, retain: retained });

  return result;
};
